// Network's pages in the five steps (docs/specs/MODULE_NAVIGATION.md § Network). The Network agent owns this file.
// Each page shows only its own part: introducers (screens/network/Introducers.tsx), events (Events.tsx), reviews and
// referral asks (Reviews.tsx, moved from Attract › Get found) and the small pages (NetworkPages.tsx).
// Screens are loaded with screen() (see ./screen.ts), never imported directly.
import { fiveSteps, type SteppedModule } from "./types";
import { screen } from "./screen";

const introducers = () => import("../screens/network/Introducers");
const events = () => import("../screens/network/Events");
const reviews = () => import("../screens/network/Reviews");
const pages = () => import("../screens/network/NetworkPages");
const IntroducerTagsPage = screen(introducers, "IntroducerTagsPage");
const IntroducersPage = screen(introducers, "IntroducersPage");
const IntroductionsPage = screen(introducers, "IntroductionsPage");
const NetworkHealthPage = screen(introducers, "NetworkHealthPage");
const EventsFoundPage = screen(events, "EventsFoundPage");
const YourEventsPage = screen(events, "YourEventsPage");
const EventResultsPage = screen(events, "EventResultsPage");
const EventsReachPage = screen(events, "EventsReachPage");
const ReviewSitesPage = screen(reviews, "ReviewSitesPage");
const ReviewsPage = screen(reviews, "ReviewsPage");
const ReferralAsksPage = screen(reviews, "ReferralAsksPage");
const ReviewResultsPage = screen(reviews, "ReviewResultsPage");
const NetworkWaitingPage = screen(pages, "NetworkWaitingPage");
const ReviewRulesPage = screen(pages, "ReviewRulesPage");
const ReferralRulesPage = screen(pages, "ReferralRulesPage");
const NetworkApprovalsPage = screen(pages, "NetworkApprovalsPage");
const NetworkCalendarPage = screen(pages, "NetworkCalendarPage");

export const network: SteppedModule = {
  key: "network",
  label: "Network",
  layout: "steps",
  paused: "Works the people who know you: introductions, communities and the contacts in your mailbox.",
  tabs: fiveSteps({
    accounts: [
      { slug: "review-sites", label: "Review sites", step: "1.45", render: () => <ReviewSitesPage /> },
      {
        slug: "labels",
        label: "Mailbox labels",
        step: "2.8",
        comingSoon: "Choose the mailbox labels Ogma reads for introductions and event invitations, so they reach Network by themselves.",
      },
      { slug: "calendar", label: "Calendar", step: "0.8", render: () => <NetworkCalendarPage /> },
      { slug: "tags", label: "Introducer and referrer tags", step: "3.10", render: () => <IntroducerTagsPage /> },
    ],
    rules: [
      { slug: "reviews", label: "Reviews", step: "10.19", render: () => <ReviewRulesPage /> },
      { slug: "referrals", label: "Referrals", step: "10.3", render: () => <ReferralRulesPage /> },
      { slug: "events-reach", label: "Events reach", step: "2.22", render: () => <EventsReachPage /> },
      { slug: "approvals", label: "Approvals", step: "E13", render: () => <NetworkApprovalsPage /> },
    ],
    research: [
      { slug: "events", label: "Events found", step: "2.22", render: () => <EventsFoundPage /> },
      { slug: "introducers", label: "Introducers and partners", step: "2.7", render: () => <IntroducersPage /> },
      {
        slug: "communities",
        label: "Communities and associations",
        comingSoon: "Trade bodies, groups and associations where your buyers gather, found and scored so you can choose which to join.",
      },
    ],
    actions: [
      { slug: "waiting", label: "Waiting for you", step: "2.23", render: () => <NetworkWaitingPage /> },
      { slug: "events", label: "Your events", step: "2.24", render: () => <YourEventsPage /> },
      { slug: "reviews", label: "Reviews", step: "1.46", render: () => <ReviewsPage /> },
      { slug: "referral-asks", label: "Referral asks", step: "10.3", render: () => <ReferralAsksPage /> },
      {
        slug: "introductions",
        label: "Introductions received",
        step: "2.8",
        comingSoon: "Introductions Ogma reads from your inbox, with the person introduced and a reply drafted for you to approve.",
      },
    ],
    performance: [
      { slug: "introductions", label: "Introductions", step: "2.7", render: () => <IntroductionsPage /> },
      { slug: "events", label: "Events", step: "2.29", render: () => <EventResultsPage /> },
      { slug: "reviews", label: "Reviews", step: "10.19", render: () => <ReviewResultsPage /> },
      { slug: "health", label: "Health", step: "2.7", render: () => <NetworkHealthPage /> },
    ],
  }),
  taskHomes: {
    choose_event: ["/network/actions/waiting"],
    approve_event_pitch: ["/network/actions/waiting"],
    // 10.19 an okay or unhappy answer to the happiness check: the owner puts it right (the review request is unaffected)
    put_it_right: ["/network/actions/waiting"],
    // 1.45 an unhappy answer to the review request: the owner gets in touch (the request stands)
    review_feedback: ["/network/actions/waiting"],
    // 1.46 an approved review reply to paste under the review
    post_review_reply: ["/network/actions/reviews"],
  },
};
